import { Box, FormControlLabel, Typography } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';

import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { LoadingButton } from '@/components/Form/LoadingButton';
import { ActionBar } from '@/components/Layout';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectOperationUser } from '@/redux/operation';

import { getDataTestId } from '@/utils/browser';

import { MinorQuestionForm } from '../MinorQuestionForm';
import { QuestionProps } from '../QuestionDrawer.interface';
import { ConfirmConsentModal } from './components/ConfirmConsentModal';
import { UsernameQuestion } from './components/UsernameQuestion';
import { useMutateMinorConsent } from './hook/useMutateMinorConsent';

import {
  FullWidthRadioGroup,
  QuestionBlock,
  QuestionBlocks,
  QuestionSection,
  QuestionTitle,
  RadioButtonContainer,
  StyledRadio,
} from '../QuestionDrawer.styled';

export type MinorConsentFormData = {
  allowToDebitCardOnlineBanking: string;
  username?: string;
};

export function MinorQuestionConsentSign({
  onAnswered,
  ...props
}: Readonly<QuestionProps>) {
  const { t, language } = useTranslate();
  const minorUser = useAppSelector(selectOperationUser);
  const {
    value: isConfirmConsentModalOpen,
    setTrue: openConfirmConsentModal,
    setFalse: closeConfirmConsentModal,
  } = useBoolean(false);

  const methods = useForm<MinorConsentFormData>({
    defaultValues: {
      allowToDebitCardOnlineBanking: undefined,
      username: '',
    },
    mode: 'onChange',
    shouldUnregister: true,
  });

  const { mutate, isPending, error } = useMutateMinorConsent({ onAnswered });

  const {
    control,
    handleSubmit,
    formState: { isValid, isValidating },
    watch,
  } = methods;

  const allowToDebitCardOnlineBanking = watch('allowToDebitCardOnlineBanking');

  const updateConsent = (formData: MinorConsentFormData) => {
    const allowToDebitCardOnlineBanking =
      formData.allowToDebitCardOnlineBanking === 'true';
    const payload = {
      allowToDebitCardOnlineBanking,
      username: allowToDebitCardOnlineBanking
        ? formData.username?.trim()
        : undefined,
    };
    mutate(payload, { onSuccess: onAnswered });
    if (isConfirmConsentModalOpen) {
      closeConfirmConsentModal();
    }
  };

  const onSubmit = handleSubmit(formData => {
    const allowToDebitCardOnlineBanking =
      formData.allowToDebitCardOnlineBanking === 'true';
    if (allowToDebitCardOnlineBanking) {
      openConfirmConsentModal();
      return;
    }
    updateConsent(formData);
  });

  return (
    <FormProvider {...methods}>
      <MinorQuestionForm
        {...props}
        isLoading={isPending}
        answerError={undefined}
        onSubmit={onSubmit}
      >
        <QuestionSection>
          <QuestionBlocks>
            <QuestionBlock>
              <QuestionTitle variant="paragraphP2">
                <Trans
                  i18nKey="questionDrawer.minorSignDocuments.question"
                  lang={language}
                  components={{
                    title: (
                      <Typography variant="bodyB1Medium" component="span" />
                    ),
                    br: <Box component="div" sx={{ paddingBottom: '1rem' }} />,
                    bold: (
                      <Typography variant="bodyB2Strong" component="span" />
                    ),
                    b: <b />,
                    li: (
                      <Typography
                        variant="paragraphP2"
                        component="li"
                        sx={{ marginLeft: '1rem' }}
                        color="neutral.800"
                      />
                    ),
                    ul: <Box component="ul" sx={{ p: 0, mb: 0 }} />,
                  }}
                />
              </QuestionTitle>
              <Controller
                name="allowToDebitCardOnlineBanking"
                control={control}
                rules={{
                  required: t(
                    'questionDrawer.minorSignDocuments.yesNoSelection.error.required',
                  ),
                }}
                render={({ field: { value, onChange } }) => (
                  <RadioButtonContainer>
                    <FullWidthRadioGroup
                      value={value || ''}
                      onChange={onChange}
                    >
                      <FormControlLabel
                        value={true}
                        control={<StyledRadio />}
                        label={t(
                          'questionDrawer.minorSignDocuments.button.yes',
                        )}
                      />
                      <FormControlLabel
                        value={false}
                        control={<StyledRadio />}
                        label={t('questionDrawer.minorSignDocuments.button.no')}
                      />
                    </FullWidthRadioGroup>
                  </RadioButtonContainer>
                )}
              />
            </QuestionBlock>
            {allowToDebitCardOnlineBanking === 'true' && (
              <UsernameQuestion
                error={error}
                isPending={isPending}
                minorUser={minorUser}
              />
            )}
            <ActionBar>
              <LoadingButton
                {...getDataTestId('minor-consent-form-submit')}
                type="submit"
                variant="contained"
                fullWidth
                isLoading={isPending}
                disabled={!isValid || isValidating || isPending}
              >
                {t('common.proceed')}
              </LoadingButton>
            </ActionBar>
          </QuestionBlocks>
        </QuestionSection>
      </MinorQuestionForm>
      <FormErrorLogger formName="MinorQuestionConsentSign" control={control} />
      <ConfirmConsentModal
        isPending={isPending}
        onConfirm={updateConsent}
        isOpen={isConfirmConsentModalOpen}
        onClose={closeConfirmConsentModal}
      />
    </FormProvider>
  );
}
